'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface DataCenterESGSectionProps {
  currentLanguage: string;
}

const DataCenterESGSection = ({ currentLanguage }: DataCenterESGSectionProps) => {
  const [activeRegion, setActiveRegion] = useState<'europe' | 'india' | 'us'>('europe');
  const [activeRegulation, setActiveRegulation] = useState<'csrd' | 'sbti' | 'ghg'>('csrd');

  const content = {
    en: {
      title: 'ESG & Carbon Credit Footprint for Global Data Centers',
      subtitle: 'Comprehensive regulatory compliance and automation solutions for Europe, India, and US data centers',
      intro: 'Navigate the complex landscape of ESG regulations with GenCarbon\'s AI-powered automation and quantum computing solutions. Our platform ensures complete compliance with CSRD, SBTi, and GHG protocols while optimizing your carbon credit footprint across multiple jurisdictions.',
      
      regions: {
        europe: {
          name: 'Europe',
          icon: 'GlobeEuropeAfricaIcon',
          overview: 'European data centers face the most stringent ESG regulations globally, with CSRD (Corporate Sustainability Reporting Directive) leading the charge for comprehensive sustainability disclosure.',
          challenges: [
            'Mandatory CSRD compliance for companies meeting size thresholds',
            'Detailed Scope 1, 2, and 3 emissions reporting requirements',
            'EU Taxonomy alignment for sustainable activities',
            'Energy Efficiency Directive (EED) compliance'
          ],
          metrics: {
            avgEmissions: '2.8 tCO2e per rack/year',
            renewableTarget: '75% by 2030',
            complianceCost: '€250K-€2M annually'
          }
        },
        india: {
          name: 'India',
          icon: 'GlobeAsiaAustraliaIcon',
          overview: 'India\'s rapidly growing data center market is witnessing increased ESG scrutiny with emerging BRSR (Business Responsibility and Sustainability Reporting) requirements and power consumption regulations.',
          challenges: [
            'BRSR Core mandatory for top 1000 listed companies',
            'State-specific renewable energy purchase obligations',
            'Water usage restrictions in water-scarce regions',
            'Carbon disclosure under voluntary frameworks'
          ],
          metrics: {
            avgEmissions: '3.2 tCO2e per rack/year',
            renewableTarget: '60% by 2030',
            complianceCost: '₹2Cr-₹15Cr annually'
          }
        },
        us: {
          name: 'United States',
          icon: 'GlobeAmericasIcon',
          overview: 'US data centers navigate a complex patchwork of federal, state, and voluntary ESG frameworks, with increasing pressure from investors and stakeholders for comprehensive climate action.',
          challenges: [
            'SEC climate disclosure rules for public companies',
            'State-level renewable energy standards (RPS)',
            'Carbon pricing mechanisms in select states',
            'Voluntary SBTi commitments and CDP reporting'
          ],
          metrics: {
            avgEmissions: '2.5 tCO2e per rack/year',
            renewableTarget: '80% by 2030',
            complianceCost: '$300K-$2.5M annually'
          }
        }
      },

      regulations: {
        csrd: {
          name: 'CSRD - Corporate Sustainability Reporting Directive',
          scope: 'European Union mandatory sustainability reporting framework',
          keyRequirements: [
            'Double materiality assessment (financial and impact)',
            'Detailed environmental, social, and governance metrics',
            'Value chain emissions (Scope 3) quantification',
            'Assurance requirements for sustainability statements',
            'Digital tagging using XBRL European Single Electronic Format'
          ],
          dataPoints: [
            'Energy consumption by source (renewable vs non-renewable)',
            'GHG emissions across Scopes 1, 2, and 3',
            'Water consumption and discharge metrics',
            'Waste generation and circular economy indicators',
            'Biodiversity impacts and mitigation measures'
          ],
          timeline: 'Phased implementation: 2024-2028 based on company size'
        },
        sbti: {
          name: 'SBTi - Science Based Targets initiative',
          scope: 'Global framework for corporate climate action aligned with Paris Agreement',
          keyRequirements: [
            'Near-term targets (5-10 years) for 1.5°C pathway',
            'Long-term net-zero targets by 2050',
            'Scope 1 and 2 emissions reduction commitments',
            'Scope 3 emissions target for value chain decarbonization',
            'Annual progress reporting and verification'
          ],
          dataPoints: [
            'Baseline emissions inventory (base year selection)',
            'Target boundary definition (Scopes included)',
            'Reduction trajectory calculations',
            'Renewable energy procurement tracking',
            'Carbon removal and offset limitations'
          ],
          timeline: 'Target submission within 2 years of commitment, achievement within 5-15 years'
        },
        ghg: {
          name: 'GHG Protocol - Greenhouse Gas Protocol',
          scope: 'International standard for corporate GHG emissions accounting and reporting',
          keyRequirements: [
            'Scope 1: Direct emissions from owned/controlled sources',
            'Scope 2: Indirect emissions from purchased electricity/heat',
            'Scope 3: All other indirect emissions in value chain',
            'Organizational and operational boundary setting',
            'Emission factor selection and documentation'
          ],
          dataPoints: [
            'Fuel consumption by type and source',
            'Electricity and steam purchases with location/market-based methods',
            'Refrigerant leakage from cooling systems',
            'Employee commuting and business travel',
            'Purchased goods and services emissions'
          ],
          timeline: 'Annual reporting cycle aligned with fiscal year'
        }
      },

      automationSolutions: {
        title: 'GenCarbon Automation & AI Solutions',
        subtitle: 'Transform ESG compliance from burden to competitive advantage',
        features: [
          {
            icon: 'CpuChipIcon',
            title: 'AI-Powered Data Collection',
            description: 'Automated real-time data extraction from IoT sensors, meters, and enterprise systems with 99.7% accuracy',
            benefits: ['Eliminates manual data entry', 'Reduces human error by 95%', 'Real-time monitoring dashboards']
          },
          {
            icon: 'ChartBarIcon',
            title: 'Quantum Computing Analytics',
            description: 'Advanced carbon footprint modeling using quantum algorithms for complex supply chain calculations',
            benefits: ['Process millions of data points simultaneously', '100x faster Scope 3 calculations', 'Scenario modeling for future projections']
          },
          {
            icon: 'DocumentCheckIcon',
            title: 'Automated Compliance Checklist',
            description: 'Dynamic regulatory requirement tracking across CSRD, SBTi, and GHG protocols with automated gap analysis',
            benefits: ['90% reduction in compliance preparation time', 'Automated report generation', 'Audit-ready documentation']
          },
          {
            icon: 'ShieldCheckIcon',
            title: 'Blockchain-Based Verification',
            description: 'Immutable audit trail for all ESG data points and carbon credit transactions',
            benefits: ['Enhanced data integrity', 'Simplified third-party assurance', 'Transparent stakeholder reporting']
          },
          {
            icon: 'BoltIcon',
            title: 'Predictive Analytics',
            description: 'Machine learning models forecast emissions trends and optimization opportunities',
            benefits: ['Identify efficiency improvements', 'Optimize renewable energy procurement', 'Predict regulatory compliance risks']
          },
          {
            icon: 'ClipboardDocumentCheckIcon',
            title: 'Integrated Reporting Platform',
            description: 'Single source of truth for all ESG metrics with multi-framework reporting capabilities',
            benefits: ['CSRD, SBTi, GHG Protocol alignment', 'Automated XBRL tagging', 'Stakeholder-specific report generation']
          }
        ]
      },

      quantumScripts: {
        title: 'Quantum Computing Scripts for Carbon Footprint',
        description: 'GenCarbon leverages quantum computing to solve complex optimization problems in carbon accounting that are intractable for classical computers.',
        applications: [
          {
            name: 'Supply Chain Emissions Mapping',
            algorithm: 'Quantum Annealing',
            benefit: 'Map entire Scope 3 value chain emissions across thousands of suppliers in minutes instead of weeks'
          },
          {
            name: 'Renewable Energy Optimization',
            algorithm: 'Variational Quantum Eigensolver (VQE)',
            benefit: 'Optimize renewable energy procurement contracts across multiple markets and time horizons'
          },
          {
            name: 'Carbon Credit Portfolio Management',
            algorithm: 'Quantum Approximate Optimization Algorithm (QAOA)',
            benefit: 'Maximize carbon credit portfolio value while ensuring regulatory compliance across jurisdictions'
          },
          {
            name: 'Data Center Cooling Optimization',
            algorithm: 'Quantum Machine Learning',
            benefit: 'Real-time optimization of cooling systems to minimize energy consumption and emissions'
          }
        ]
      },

      caseStudy: {
        title: 'Success Story: European Hyperscale Data Center',
        client: 'Leading cloud service provider with 15 data centers across EU',
        challenge: 'Manual CSRD compliance process taking 6 months per reporting cycle with data quality issues',
        solution: 'Implemented GenCarbon AI automation platform with quantum analytics',
        results: [
          '92% reduction in compliance preparation time (from 6 months to 3 weeks)',
          '€1.2M annual cost savings through automated data collection',
          'Achieved SBTi validation 18 months ahead of schedule',
          'Identified €3.5M in energy efficiency opportunities through AI analytics',
          'Reduced Scope 2 emissions by 35% through optimized renewable procurement'
        ]
      },

      cta: {
        title: 'Ready to Transform Your Data Center ESG Compliance?',
        description: 'Schedule a personalized demo to see how GenCarbon can automate your regulatory compliance and optimize your carbon footprint.',
        primaryButton: 'Schedule Demo',
        secondaryButton: 'Download White Paper'
      }
    },
    hi: {
      title: 'वैश्विक डेटा सेंटरों के लिए ESG और कार्बन क्रेडिट फुटप्रिंट',
      subtitle: 'यूरोप, भारत और अमेरिकी डेटा सेंटरों के लिए व्यापक नियामक अनुपालन और स्वचालन समाधान',
      intro: 'GenCarbon के AI-संचालित स्वचालन और क्वांटम कंप्यूटिंग समाधानों के साथ ESG नियमों के जटिल परिदृश्य को नेविगेट करें।',
      // ... (Hindi translations follow same structure)
    },
    ar: {
      title: 'بصمة ESG والائتمان الكربوني لمراكز البيانات العالمية',
      subtitle: 'حلول الامتثال التنظيمي والأتمتة الشاملة لمراكز البيانات في أوروبا والهند والولايات المتحدة',
      intro: 'تنقل في المشهد المعقد لأنظمة ESG باستخدام حلول الأتمتة المدعومة بالذكاء الاصطناعي والحوسبة الكمومية من GenCarbon.',
      // ... (Arabic translations follow same structure)
    }
  };

  const lang = content[currentLanguage as keyof typeof content] || content.en;
  const currentRegion = lang.regions[activeRegion];
  const currentRegulation = lang.regulations[activeRegulation];

  return (
    <section className="py-16 bg-gradient-to-b from-background to-surface">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-text-primary mb-4">
            {lang.title}
          </h2>
          <p className="text-xl text-primary font-semibold mb-4">
            {lang.subtitle}
          </p>
          <p className="text-lg text-text-secondary max-w-4xl mx-auto leading-relaxed">
            {lang.intro}
          </p>
        </div>

        {/* Regional Analysis Section */}
        <div className="mb-16">
          <h3 className="font-heading font-bold text-2xl md:text-3xl text-text-primary mb-8 text-center">
            Regional ESG Landscape Analysis
          </h3>
          
          {/* Region Selector */}
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            {Object.entries(lang.regions).map(([key, region]) => (
              <button
                key={key}
                onClick={() => setActiveRegion(key as 'europe' | 'india' | 'us')}
                className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all ${
                  activeRegion === key
                    ? 'bg-primary text-primary-foreground shadow-lg'
                    : 'bg-card border border-border text-text-secondary hover:bg-surface'
                }`}
              >
                <Icon name={region.icon as any} size={24} />
                {region.name}
              </button>
            ))}
          </div>

          {/* Region Content */}
          <div className="bg-card border border-border rounded-xl p-8 shadow-natural">
            <h4 className="font-heading font-bold text-xl text-primary mb-4">
              {currentRegion.name} Data Center ESG Overview
            </h4>
            <p className="text-text-secondary mb-6 leading-relaxed">
              {currentRegion.overview}
            </p>

            {/* Challenges */}
            <div className="mb-6">
              <h5 className="font-semibold text-lg text-text-primary mb-3">Key Challenges:</h5>
              <ul className="space-y-2">
                {currentRegion.challenges.map((challenge, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-text-secondary">
                    <Icon name="CheckCircleIcon" size={20} className="text-primary mt-1 flex-shrink-0" />
                    <span>{challenge}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Metrics */}
            <div className="grid md:grid-cols-3 gap-4 bg-surface rounded-lg p-6">
              <div>
                <div className="text-sm text-text-secondary mb-1">Avg. Emissions</div>
                <div className="font-bold text-xl text-primary">{currentRegion.metrics.avgEmissions}</div>
              </div>
              <div>
                <div className="text-sm text-text-secondary mb-1">Renewable Target</div>
                <div className="font-bold text-xl text-conversion">{currentRegion.metrics.renewableTarget}</div>
              </div>
              <div>
                <div className="text-sm text-text-secondary mb-1">Annual Compliance Cost</div>
                <div className="font-bold text-xl text-secondary">{currentRegion.metrics.complianceCost}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Regulation Deep Dive */}
        <div className="mb-16">
          <h3 className="font-heading font-bold text-2xl md:text-3xl text-text-primary mb-8 text-center">
            ESG Regulation Framework Analysis
          </h3>

          {/* Regulation Selector */}
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            {Object.entries(lang.regulations).map(([key, reg]) => (
              <button
                key={key}
                onClick={() => setActiveRegulation(key as 'csrd' | 'sbti' | 'ghg')}
                className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                  activeRegulation === key
                    ? 'bg-conversion text-conversion-foreground shadow-lg'
                    : 'bg-card border border-border text-text-secondary hover:bg-surface'
                }`}
              >
                {reg.name.split(' - ')[0]}
              </button>
            ))}
          </div>

          {/* Regulation Content */}
          <div className="bg-card border border-border rounded-xl p-8 shadow-natural">
            <h4 className="font-heading font-bold text-2xl text-conversion mb-2">
              {currentRegulation.name}
            </h4>
            <p className="text-text-secondary mb-6 italic">{currentRegulation.scope}</p>

            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h5 className="font-semibold text-lg text-text-primary mb-3">Key Requirements:</h5>
                <ul className="space-y-2">
                  {currentRegulation.keyRequirements.map((req, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-text-secondary">
                      <Icon name="DocumentCheckIcon" size={20} className="text-conversion mt-1 flex-shrink-0" />
                      <span>{req}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h5 className="font-semibold text-lg text-text-primary mb-3">Critical Data Points:</h5>
                <ul className="space-y-2">
                  {currentRegulation.dataPoints.map((point, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-text-secondary">
                      <Icon name="ChartBarIcon" size={20} className="text-primary mt-1 flex-shrink-0" />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="mt-6 p-4 bg-accent/10 border border-accent/20 rounded-lg">
              <div className="flex items-start gap-2">
                <Icon name="CalendarIcon" size={20} className="text-accent mt-1" />
                <div>
                  <strong className="text-accent">Implementation Timeline:</strong>
                  <span className="text-text-secondary ml-2">{currentRegulation.timeline}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Automation Solutions */}
        <div className="mb-16 bg-gradient-to-br from-primary/5 to-secondary/5 rounded-2xl p-8 md:p-12">
          <div className="text-center mb-12">
            <h3 className="font-heading font-bold text-2xl md:text-3xl text-text-primary mb-4">
              {lang.automationSolutions.title}
            </h3>
            <p className="text-xl text-primary font-semibold">
              {lang.automationSolutions.subtitle}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {lang.automationSolutions.features.map((feature, idx) => (
              <div key={idx} className="bg-card border border-border rounded-xl p-6 shadow-natural hover:shadow-natural-lg transition-all">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <Icon name={feature.icon as any} size={24} className="text-primary" />
                </div>
                <h4 className="font-heading font-bold text-lg text-text-primary mb-3">
                  {feature.title}
                </h4>
                <p className="text-text-secondary mb-4 leading-relaxed">
                  {feature.description}
                </p>
                <ul className="space-y-2">
                  {feature.benefits.map((benefit, bidx) => (
                    <li key={bidx} className="flex items-start gap-2 text-sm text-text-secondary">
                      <Icon name="CheckIcon" size={16} className="text-conversion mt-0.5 flex-shrink-0" />
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Quantum Computing Section */}
        <div className="mb-16">
          <div className="text-center mb-12">
            <h3 className="font-heading font-bold text-2xl md:text-3xl text-text-primary mb-4">
              {lang.quantumScripts.title}
            </h3>
            <p className="text-lg text-text-secondary max-w-3xl mx-auto">
              {lang.quantumScripts.description}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {lang.quantumScripts.applications.map((app, idx) => (
              <div key={idx} className="bg-card border border-border rounded-xl p-6 shadow-natural">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-secondary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name="CpuChipIcon" size={20} className="text-secondary" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-heading font-bold text-lg text-text-primary mb-2">
                      {app.name}
                    </h4>
                    <div className="inline-block px-3 py-1 bg-secondary/10 rounded-full text-sm text-secondary font-semibold mb-3">
                      {app.algorithm}
                    </div>
                    <p className="text-text-secondary leading-relaxed">
                      {app.benefit}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Case Study */}
        <div className="mb-16 bg-gradient-to-br from-conversion/5 to-accent/5 rounded-2xl p-8 md:p-12">
          <h3 className="font-heading font-bold text-2xl md:text-3xl text-text-primary mb-6 text-center">
            {lang.caseStudy.title}
          </h3>
          
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="bg-card rounded-lg p-4 border border-border">
                <div className="text-sm text-text-secondary mb-1">Client</div>
                <div className="font-semibold text-text-primary">{lang.caseStudy.client}</div>
              </div>
              <div className="bg-card rounded-lg p-4 border border-border">
                <div className="text-sm text-text-secondary mb-1">Challenge</div>
                <div className="font-semibold text-text-primary">{lang.caseStudy.challenge}</div>
              </div>
              <div className="bg-card rounded-lg p-4 border border-border">
                <div className="text-sm text-text-secondary mb-1">Solution</div>
                <div className="font-semibold text-text-primary">{lang.caseStudy.solution}</div>
              </div>
            </div>

            <div className="bg-card rounded-xl p-6 border border-border">
              <h4 className="font-heading font-bold text-lg text-conversion mb-4">Results Achieved:</h4>
              <ul className="space-y-3">
                {lang.caseStudy.results.map((result, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <Icon name="CheckCircleIcon" size={24} className="text-conversion mt-0.5 flex-shrink-0" />
                    <span className="text-text-primary font-medium">{result}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center bg-gradient-to-r from-primary to-secondary rounded-2xl p-8 md:p-12 text-white">
          <h3 className="font-heading font-bold text-2xl md:text-3xl mb-4">
            {lang.cta.title}
          </h3>
          <p className="text-lg mb-8 opacity-90 max-w-2xl mx-auto">
            {lang.cta.description}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="#consultation"
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-primary font-heading font-semibold rounded-lg hover:bg-gray-100 shadow-lg transition-all"
            >
              {lang.cta.primaryButton}
              <Icon name="ArrowRightIcon" size={20} className="ml-2" />
            </a>
            <button className="inline-flex items-center justify-center px-8 py-4 bg-transparent border-2 border-white text-white font-heading font-semibold rounded-lg hover:bg-white/10 transition-all">
              <Icon name="DocumentArrowDownIcon" size={20} className="mr-2" />
              {lang.cta.secondaryButton}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DataCenterESGSection;